import { Component, inject, input, model, OnChanges, OnInit, output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { AccountingModelDetailModel } from 'src/app/models/management/accounting-model-detail.model';
import { AccountingModelModel } from 'src/app/models/management/accounting-model.model';
import { AccountingModelDetailService } from 'src/app/services/management/accounting-model-detail.service';
import { ConceptFamilyService } from 'src/app/services/management/concept-family.service';
import { ConceptInvoicingService } from 'src/app/services/invoicing/concept-invoicing.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-accounting-model-detail',
  templateUrl: './register-accounting-model-detail.component.html',
  styleUrls: ['./register-accounting-model-detail.component.scss'],
  standalone: false
})
export class RegisterAccountingModelDetailComponent implements OnInit, OnChanges {

  private readonly accountingModelDetailService = inject(AccountingModelDetailService);
  private readonly conceptFamilyService = inject(ConceptFamilyService);
  private readonly conceptInvoicingService = inject(ConceptInvoicingService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly formBuilder = inject(FormBuilder);

  accountingModelModel = input<AccountingModelModel>(new AccountingModelModel());
  accountingModelDetailContract = model<AccountingModelDetailModel>(new AccountingModelDetailModel());
  isFormView = output<boolean>();
  updateListDetail = output<void>();

  formAccountingModelDetail: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  listConceptInvoicing: any[] = [];
  listFamily: any[] = [];

  async ngOnInit() {
    this.validateForm(this.accountingModelDetailContract());
    await this.getAllLists();
  }

  ngOnChanges(changes: SimpleChanges) {
    const accountingModelDetailContract = changes['accountingModelDetailContract'];
    if (accountingModelDetailContract && !accountingModelDetailContract.firstChange) {
      this.validateForm(this.accountingModelDetailContract());
    }
  }

  clearForm() {
    this.isFormView.emit(false);
    this.accountingModelDetailContract.set(new AccountingModelDetailModel());
    this.validateForm(this.accountingModelDetailContract());
    this.listArticle = [];
    this.listFamily = [];
  }

  async getAllLists(
    ...listsToLoad: Array<'conceptInvoicing' | 'families' | 'articles'>
  ) {
    try {
      const lists = {
        conceptInvoicing: async () => {
          this.listConceptInvoicing = await firstValueFrom(
            this.conceptInvoicingService.getAllConceptsInvoicingForSelectAsync()
          );
        },
        families: async () => {
          this.listFamily = await firstValueFrom(
            this.conceptFamilyService.getConceptFamiliesByConceptInvoicingIdForSelectAsync(this.formAccountingModelDetail.get('conceptInvoicingId')?.value ?? 0)
          );
        },
        articles: async () => {
          this.listArticle = await firstValueFrom(
            this.conceptFamilyService.getArticlesByFamilyIdForSelectAsync(this.formAccountingModelDetail.get('familyId')?.value ?? 0)
          );
        }
      };

      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.conceptInvoicing(),
          lists.families(),
          lists.articles()
        ]);
      }
    } catch (error) {
      console.error('Error al obtener las listas para el detalle del modelo contable.', error);
    }
  }

  async getConceptInvoicing(event: number, clearAccounts: boolean) {
    await this.getAllLists('families');
    this.listArticle = [];
    this.formAccountingModelDetail.get('familyId')?.patchValue(null);
    this.formAccountingModelDetail.get('articleId')?.patchValue(null);
    const selectedConcept = this.listConceptInvoicing.find(x => x.value === event);
    if (selectedConcept) {
      if (selectedConcept.swDispersedFamily) {
        this.formAccountingModelDetail.get('familyId')?.enable();
        this.formAccountingModelDetail.get('articleId')?.enable();
      } else {
        this.formAccountingModelDetail.get('familyId')?.disable();
        this.formAccountingModelDetail.get('articleId')?.disable();
      }
      if (clearAccounts) {
        this.formAccountingModelDetail.get('salesAccount')?.patchValue(null);
        this.formAccountingModelDetail.get('salesReturnAccount')?.patchValue(null);
        this.formAccountingModelDetail.get('accountingCostCenter')?.patchValue(null);
      }
    }
  }

  async getFamily(event: number, clearAccounts: boolean) {
    this.formAccountingModelDetail.get('articleId')?.patchValue(null);
    const selectedFamily = this.listFamily.find(x => x.value === event);
    if (selectedFamily) {
      if (selectedFamily.swDispersedArticle) {
        await this.getAllLists('articles');
        this.formAccountingModelDetail.get('articleId')?.enable();
      } else {
        this.formAccountingModelDetail.get('articleId')?.disable();
      }
      if (clearAccounts) {
        this.formAccountingModelDetail.get('salesAccount')?.patchValue(null);
        this.formAccountingModelDetail.get('salesReturnAccount')?.patchValue(null);
        this.formAccountingModelDetail.get('accountingCostCenter')?.patchValue(null);
      }
    }
  }

  async send() {
    if (!this.formAccountingModelDetail.valid) {
      this.showValidationMessages();
      return;
    }
    const modelAccountingModelDetail = this.formAccountingModelDetail.getRawValue() as AccountingModelDetailModel;

    if (modelAccountingModelDetail.salesAccount?.trim() === '') modelAccountingModelDetail.salesAccount = null;
    if (modelAccountingModelDetail.salesReturnAccount?.trim() === '') modelAccountingModelDetail.salesReturnAccount = null;
    if (modelAccountingModelDetail.accountingCostCenter?.trim() === '') modelAccountingModelDetail.accountingCostCenter = null;

    if (modelAccountingModelDetail.familyId === 0 || modelAccountingModelDetail.familyId === null) {
      modelAccountingModelDetail.familyId = null;
      const selectedConcept = this.listConceptInvoicing.find(x => x.value === modelAccountingModelDetail.conceptInvoicingId);
      if (selectedConcept && selectedConcept.swDispersedFamily) {
        this.toastHandlerService.showToast('warning', 'El concepto dispersa familia y no tiene definida una familia.');
        return;
      }
    }
    if (modelAccountingModelDetail.articleId === 0 || modelAccountingModelDetail.articleId === null) {
      modelAccountingModelDetail.articleId = null;
      const selectedFamily = this.listFamily.find(x => x.value === modelAccountingModelDetail.familyId);
      if (selectedFamily && selectedFamily.swDispersedArticle) {
        this.toastHandlerService.showToast('warning', 'La familia dispersa artículo y no tiene definido un artículo.');
        return;
      }
    }
    if (modelAccountingModelDetail.salesAccount === null) {
      this.toastHandlerService.showToast('warning', 'Falta definir el número de cuenta de venta.');
      return;
    }
    if (modelAccountingModelDetail.accountingCostCenter === null) {
      const selectedFamily = this.listFamily.find(x => x.value === modelAccountingModelDetail.familyId);
      if (selectedFamily && selectedFamily.swDispersedArticle) {
        this.toastHandlerService.showToast('warning', 'No se ha definido el centro de costo contable para el artículo.');
        return;
      }
    }

    if (this.accountingModelDetailContract().accountingModelDetailId === 0) {
      modelAccountingModelDetail.accountingModelId = this.accountingModelModel().accountingModelId;
      try {
        await firstValueFrom(this.accountingModelDetailService.createAccountingModelDetail(modelAccountingModelDetail));
        this.toastHandlerService.showToast('success', 'El detalle del modelo contable fue creado exitosamente.');
        this.updateListDetail.emit();
        this.clearForm();
      } catch (error) {
        console.error('Error al crear el detalle del modelo contable.', error);
      }
    } else {
      try {
        const detailToUpdate = { ...this.accountingModelDetailContract(), ...modelAccountingModelDetail };
        await firstValueFrom(this.accountingModelDetailService.updateAccountingModelDetail(detailToUpdate));
        this.toastHandlerService.showToast('success', 'El detalle del modelo contable fue modificado exitosamente.');
        this.updateListDetail.emit();
        this.clearForm();
      } catch (error) {
        console.error('Error al actualizar el detalle del modelo contable.', error);
      }
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      conceptInvoicingId: {
        required: 'El concepto de facturación es obligatorio.',
        min: 'Debe seleccionar un concepto de facturación válido.'
      },
      salesAccount: {
        maxlength: 'La cuenta de venta no puede tener más de 10 caracteres.'
      },
      salesReturnAccount: {
        maxlength: 'La cuenta de devolución no puede tener más de 10 caracteres.'
      },
      accountingCostCenter: {
        maxlength: 'El centro de costo contable no puede tener más de 10 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formAccountingModelDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  async validateForm(accountingModelDetail: AccountingModelDetailModel) {
    if (accountingModelDetail.conceptInvoicingId != 0) {
      this.listFamily = await firstValueFrom(
        this.conceptFamilyService.getConceptFamiliesByConceptInvoicingIdForSelectAsync(accountingModelDetail.conceptInvoicingId)
      );
    }
    if (accountingModelDetail.familyId != 0 && accountingModelDetail.familyId != null) {
      this.listArticle = await firstValueFrom(
        this.conceptFamilyService.getArticlesByFamilyIdForSelectAsync(accountingModelDetail.familyId)
      );
    }
    this.formAccountingModelDetail = this.formBuilder.group({
      conceptInvoicingId: [accountingModelDetail.conceptInvoicingId, [Validators.required, Validators.min(1)]],
      familyId: [accountingModelDetail.familyId],
      articleId: [accountingModelDetail.articleId],
      salesAccount: [accountingModelDetail.salesAccount, [Validators.maxLength(10)]],
      salesReturnAccount: [accountingModelDetail.salesReturnAccount, [Validators.maxLength(10)]],
      accountingCostCenter: [accountingModelDetail.accountingCostCenter, [Validators.maxLength(10)]]
    });
    if (accountingModelDetail.conceptInvoicingId != 0) {
      if (accountingModelDetail.conceptInvoicingId != null && accountingModelDetail.conceptInvoicingId != 0 &&
        (accountingModelDetail.familyId == null || accountingModelDetail.familyId == 0)) {
        await this.getConceptInvoicing(accountingModelDetail.conceptInvoicingId, false);
      }
      if (accountingModelDetail.familyId != 0 && accountingModelDetail.familyId != null) {
        this.getFamily(accountingModelDetail.familyId, false);
        if (accountingModelDetail.articleId != 0 && accountingModelDetail.articleId != null) {
          this.formAccountingModelDetail.get('articleId')?.patchValue(accountingModelDetail.articleId);
        }
      }
    }
  }

}
